import React, { forwardRef, useCallback } from 'react';

import cn from 'classnames';
import { noop } from 'lodash';
import { bool, func, node, string } from 'prop-types';

import s from './ClickableComponent.css';

/**
 * Wrapper used to allow interactivity for presentational components
 *
 * @param {string} className - used to apply additional styles
 * @param {node} children - content to render within component
 * @param {bool} disabled - if true, onClick will not fire
 * @param {string} href - link to new page
 * @param {func} onClick - function that fires when element is clicked
 * @param {string} tagName - prop for changing the tag name for clickable component.
 * @param {bool} fullWidth - if true, has width of 100%
 * It should only be used for hotfix purposes. For general use don't need to pass this prop.
 */
const ClickableComponent = forwardRef(
  (
    {
      tagName: Tag,
      className,
      children,
      disabled,
      href,
      onClick,
      fullWidth,
      testId,
      ...rest
    },
    ref,
  ) => {
    const handleClick = useCallback(
      event => {
        if (disabled) {
          return;
        }
        onClick(event);
      },
      [disabled, onClick],
    );

    const restProps = {
      ...rest,
      ...(Tag === 'button' ? { type: 'button' } : {}),
    };

    return (
      <Tag
        className={cn(s.root, className, {
          [s.pointer]: !disabled,
          [s.fullWidth]: fullWidth,
        })}
        disabled={disabled}
        onClick={handleClick}
        ref={ref}
        href={!disabled && !!href ? href : undefined}
        data-testid={testId}
        {...restProps}
      >
        {children}
      </Tag>
    );
  },
);

ClickableComponent.propTypes = {
  children: node,
  className: string,
  disabled: bool,
  href: string,
  onClick: func,
  tagName: string,
  fullWidth: bool,
  testId: string,
};

ClickableComponent.defaultProps = {
  children: null,
  className: '',
  disabled: false,
  href: null,
  onClick: noop,
  tagName: 'button',
  fullWidth: false,
  testId: undefined,
};

export default ClickableComponent;
